import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useBreakpoint from '../hooks/useBreakpoint';
import './LandingPage.css';

/* ── Design tokens — FrontUp style ── */
const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  white:  '#fff',
  font:   "'Inter', system-ui, sans-serif",
};

/* ── Icons ── */
const Ico = {
  bell: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  card: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  file: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  shield: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  chart: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  home: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  wrench: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  ),
  check: ({ color = T.ink, size = 15 } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  x: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  plus: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  minus: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
};

/* ── Data ── */
const FEATURES = [
  {
    icon: Ico.bell,
    title: 'Mahnungen erstellen',
    desc: 'Erstelle und versende Mahnungen direkt aus dem System — schnell und ohne Umwege.',
  },
  {
    icon: Ico.card,
    title: 'Zahlungsübersicht',
    desc: 'Behalte offene und bezahlte Mieten im Blick. Monatlich. Für jede Einheit.',
  },
  {
    icon: Ico.file,
    title: 'Betriebskostenabrechnung',
    desc: 'Bereite deine Abrechnung strukturiert vor — Kosten erfassen, Schlüssel anwenden, PDF exportieren.',
  },
  {
    icon: Ico.chart,
    title: 'Dokumente & Berichte',
    desc: 'Alle Mietverträge, Abrechnungen und Nachweise zentral an einem Ort. Jederzeit abrufbar.',
  },
  {
    icon: Ico.home,
    title: 'Objekte & Mieter',
    desc: 'Alle Objekte, Einheiten und Mieter zentral verwalten — übersichtlich und schnell erfasst.',
  },
  {
    icon: Ico.shield,
    title: 'Kautionsmanagement',
    desc: 'Kautionen erfassen und dokumentieren. (Funktion in Entwicklung — bald verfügbar.)',
  },
];

const USE_CASES = [
  {
    icon: Ico.home,
    title: 'Privatvermieter',
    desc: 'Für 1–10 Einheiten. Behalte den Überblick ohne eigene Hausverwaltung.',
  },
  {
    icon: Ico.users,
    title: 'Hausverwaltungen',
    desc: 'Verwalte dutzende Objekte und Mieter — ohne doppelte Datenpflege.',
  },
  {
    icon: Ico.chart,
    title: 'Investoren',
    desc: 'Cashflow-Übersicht, Auslastung und Rendite immer im Blick.',
  },
  {
    icon: Ico.bell,
    title: 'Mahnwesen',
    desc: 'Mahnungen erstellen, Fristen setzen und den Status im Blick behalten.',
  },
  {
    icon: Ico.file,
    title: 'Nebenkostenabrechnung',
    desc: 'Alle Posten erfassen, auf Mieter verteilen, als PDF exportieren.',
  },
  {
    icon: Ico.wrench,
    title: 'Instandhaltung',
    desc: 'Anfragen von Mietern zentral verwalten, Vorgänge dokumentieren.',
  },
];

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '29 €',
    period: '/ Monat',
    desc: 'Für private Vermieter mit einem kleinen Portfolio.',
    features: [
      { label: 'Bis zu 10 Einheiten', ok: true },
      { label: 'Unbegrenzte Mieter', ok: true },
      { label: 'Zahlungen erfassen & verfolgen', ok: true },
      { label: 'Mahnungen erstellen', ok: true },
      { label: 'PDF Export', ok: true },
    ],
    cta: '14 Tage kostenlos →',
    ctaLink: '/register',
  },
  {
    key: 'professional',
    name: 'Professional',
    price: '79 €',
    period: '/ Monat',
    desc: 'Für wachsende Portfolios mit mehr Anforderungen.',
    highlight: true,
    features: [
      { label: 'Bis zu 100 Einheiten', ok: true },
      { label: 'Unbegrenzte Mieter', ok: true },
      { label: 'Zahlungen verwalten & Mahnungen versenden', ok: true },
      { label: 'Betriebskosten vorbereiten', ok: true },
      { label: 'Dokumente & Berichte', ok: true },
      { label: 'Prioritäts-Support', ok: true },
    ],
    cta: '14 Tage kostenlos →',
    ctaLink: '/register',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: '199 €',
    period: '/ Monat',
    desc: 'Für professionelle Verwalter und Teams.',
    features: [
      { label: 'Unbegrenzte Einheiten', ok: true },
      { label: 'Alles aus Professional', ok: true },
      { label: 'Team-Nutzung (mehrere Nutzer)', ok: true },
      { label: 'Erweiterter Support', ok: true },
      { label: 'Individuelle Einrichtung', ok: true },
    ],
    cta: 'Kontakt aufnehmen →',
    ctaLink: '/register',
  },
];

const FAQ_ITEMS = [
  {
    q: 'Wie lange dauert die Einrichtung?',
    a: 'In wenigen Minuten. Konto erstellen, erstes Objekt anlegen, Mieter hinzufügen — und das System läuft. Kein Vertrag, keine Kreditkarte für den Start.',
  },
  {
    q: 'Kann ich jederzeit kündigen?',
    a: 'Ja. Monatlich kündbar, keine Mindestlaufzeit, keine Kündigungsgebühr. Wenn du nicht zufrieden bist, zahlst du einfach nicht weiter.',
  },
  {
    q: 'Ist ImmoHQ DSGVO-konform?',
    a: 'Ja. Unsere Server stehen in Deutschland, alle Daten bleiben in der EU. Wir erfüllen alle DSGVO-Anforderungen vollständig.',
  },
  {
    q: 'Was passiert wenn ich mein Einheitenlimit erreiche?',
    a: 'Du kannst jederzeit auf einen höheren Plan wechseln — ohne Datenverlust. Der Wechsel ist sofort wirksam und wird anteilig abgerechnet.',
  },
  {
    q: 'Können meine Mieter ImmoHQ auch nutzen?',
    a: 'Ja, im Professional- und Enterprise-Plan. Du lädst sie per E-Mail ein und sie erhalten Zugang zu ihrem eigenen Portal — Zahlungshistorie, Dokumente, Anfragen.',
  },
  {
    q: 'Wie funktioniert die Betriebskostenabrechnung?',
    a: 'Du trägst alle Kostenposten ein und wählst den Verteilungsschlüssel. ImmoHQ berechnet die Anteile je Mieter — das Ergebnis kannst du als PDF exportieren und direkt versenden.',
  },
];

const IMMOBILIEN_TYPES = [
  {
    slug: 'mehrfamilienhaus',
    label: 'Mehrfamilienhaus',
    sub: 'Bis zu 50 Einheiten',
    img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'einfamilienhaus',
    label: 'Einfamilienhaus',
    sub: 'Privat & kompakt',
    img: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'eigentumswohnung',
    label: 'Eigentumswohnung',
    sub: 'ETW & Eigentümer',
    img: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'gewerbeimmobilie',
    label: 'Gewerbeimmobilie',
    sub: 'Büro & Laden',
    img: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'neubauprojekt',
    label: 'Neubauprojekt',
    sub: 'Erstbezug & Planung',
    img: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'dachgeschoss',
    label: 'Dachgeschoss',
    sub: 'Penthouse & Maisonette',
    img: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=720&q=80&fit=crop&auto=format',
  },
  {
    slug: 'kapitalanlage',
    label: 'Kapitalanlage',
    sub: 'Rendite & Portfolio',
    img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=720&q=80&fit=crop&auto=format',
  },
];

/* ── Pill label ── */
function Pill({ children }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      background: '#f5f5f5', border: `1px solid ${T.border}`,
      borderRadius: 100, padding: '5px 14px', marginBottom: 22,
    }}>
      <div style={{ width: 6, height: 6, background: T.ink, borderRadius: '50%' }} />
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: T.ink }}>
        {children}
      </span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [scrolled, setScrolled]             = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq]               = useState(null);

  /* slideshow */
  const slideRef   = useRef(null);
  const dragState  = useRef({ active: false, startX: 0, scrollLeft: 0 });

  function slideScroll(dir) {
    if (!slideRef.current) return;
    slideRef.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
  }
  function onDragStart(e) {
    dragState.current = { active: true, startX: e.pageX, scrollLeft: slideRef.current.scrollLeft };
    slideRef.current.style.cursor = 'grabbing';
  }
  function onDragMove(e) {
    if (!dragState.current.active) return;
    const dx = e.pageX - dragState.current.startX;
    slideRef.current.scrollLeft = dragState.current.scrollLeft - dx;
  }
  function onDragEnd() {
    dragState.current.active = false;
    if (slideRef.current) slideRef.current.style.cursor = 'grab';
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ fontFamily: T.font, color: T.ink, background: T.white, overflowX: 'hidden' }}>

      {/* ══════════ NAV ══════════ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: scrolled ? 'rgba(255,255,255,0.97)' : T.white,
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(16px, 5vw, 40px)', height: 64,
        transition: 'all 0.2s',
        boxShadow: scrolled ? '0 1px 12px rgba(0,0,0,0.06)' : 'none',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.3px', color: T.ink }}>
            ImmoHQ
          </span>
        </div>

        {/* Center nav */}
        <div className="lp-nav-links" style={{ display: 'flex', gap: 4, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {[['#features', 'Features'], ['#how-it-works', 'So funktioniert\'s'], ['#pricing', 'Preise']].map(([href, label]) => (
            <a key={href} href={href} style={{
              display: 'flex', alignItems: 'center', height: 64,
              padding: '0 14px', fontSize: 14, fontWeight: 500, color: '#555',
              textDecoration: 'none', transition: 'color 0.15s', whiteSpace: 'nowrap',
            }}
              onMouseEnter={e => e.target.style.color = T.ink}
              onMouseLeave={e => e.target.style.color = '#555'}
            >
              {label}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="lp-nav-cta" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {user ? (
            <button onClick={() => navigate('/dashboard')} style={{
              padding: '9px 20px', background: T.ink, color: T.white,
              borderRadius: 8, fontSize: 14, fontWeight: 600,
              border: 'none', cursor: 'pointer', transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#333'}
              onMouseLeave={e => e.currentTarget.style.background = T.ink}
            >
              Zum Dashboard →
            </button>
          ) : (
            <>
              <Link to="/login" style={{
                padding: '9px 18px', border: `1.5px solid ${T.border}`,
                borderRadius: 8, fontSize: 14, fontWeight: 600, color: T.ink,
                background: T.white, textDecoration: 'none', transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.ink}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                Anmelden
              </Link>
              <Link to="/register" style={{
                padding: '9px 20px', background: T.ink, color: T.white,
                borderRadius: 8, fontSize: 14, fontWeight: 600,
                textDecoration: 'none', transition: 'background 0.15s',
                whiteSpace: 'nowrap',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#333'}
                onMouseLeave={e => e.currentTarget.style.background = T.ink}
              >
                Kostenlos starten
              </Link>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className="lp-hamburger"
          onClick={() => setMobileMenuOpen(v => !v)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.2" strokeLinecap="round">
            {mobileMenuOpen
              ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
            }
          </svg>
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div style={{
          position: 'fixed', top: 64, left: 0, right: 0, zIndex: 199,
          background: T.white, borderBottom: `1px solid ${T.border}`,
          padding: '16px 24px 28px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        }}>
          {[['#features', 'Features'], ['#how-it-works', 'So funktioniert\'s'], ['#pricing', 'Preise']].map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMobileMenuOpen(false)} style={{
              display: 'block', fontSize: 15, fontWeight: 500, color: T.ink,
              textDecoration: 'none', padding: '12px 0', borderBottom: `1px solid ${T.border}`,
            }}>
              {label}
            </a>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            {user ? (
              <button onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }} style={{
                textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.white,
                background: T.ink, border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer',
              }}>Zum Dashboard →</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{
                  textAlign: 'center', fontSize: 14, fontWeight: 600, color: T.ink,
                  textDecoration: 'none', padding: '12px', border: `1.5px solid ${T.border}`, borderRadius: 8,
                }}>Anmelden</Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} style={{
                  textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.white,
                  background: T.ink, textDecoration: 'none', padding: '12px', borderRadius: 8,
                }}>Kostenlos starten</Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════ HERO ══════════ */}
      <section style={{
        background: T.white,
        padding: 'clamp(56px, 10vw, 88px) clamp(16px, 5vw, 40px)',
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1.05fr .95fr',
          gap: 72, alignItems: 'center',
        }} className="lp-hero-grid">

          {/* Left */}
          <div>
            <Pill>Immobilienverwaltung · Jetzt in Deutschland</Pill>

            <h1 style={{
              fontSize: 'clamp(44px, 5.5vw, 72px)', fontWeight: 800,
              lineHeight: 1.04, letterSpacing: '-2.5px', color: T.ink,
              margin: '0 0 20px',
            }}>
              Kein Chaos.<br />Kein Excel.<br />Nur Klarheit.
            </h1>

            <p style={{
              fontSize: 17, color: '#666', lineHeight: 1.7,
              maxWidth: 480, margin: '0 0 32px',
            }}>
              ImmoHQ bündelt deine Immobilienverwaltung an einem Ort. Behalte den Überblick über Mieter,
              Zahlungen und offene Posten – einfach und klar.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 36 }}>
              {user ? (
                <button onClick={() => navigate('/dashboard')} style={{
                  padding: '14px 28px', background: T.ink, color: T.white,
                  borderRadius: 8, fontSize: 15, fontWeight: 700,
                  border: 'none', cursor: 'pointer', transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#333'}
                  onMouseLeave={e => e.currentTarget.style.background = T.ink}
                >
                  Zum Dashboard →
                </button>
              ) : (
                <Link to="/register" style={{
                  padding: '14px 28px', background: T.ink, color: T.white,
                  borderRadius: 8, fontSize: 15, fontWeight: 700,
                  textDecoration: 'none', transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#333'}
                  onMouseLeave={e => e.currentTarget.style.background = T.ink}
                >
                  Kostenlos starten
                </Link>
              )}
              <a href="#how-it-works" style={{
                padding: '13px 28px', background: T.white, color: T.ink,
                border: `1.5px solid ${T.border}`, borderRadius: 8,
                fontSize: 15, fontWeight: 600, textDecoration: 'none',
                transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.ink}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                So funktioniert's
              </a>
            </div>

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {['Keine Kreditkarte', 'DSGVO-konform', 'Server in DE', 'Jederzeit kündbar'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 500, color: T.muted }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Right — Dashboard preview card */}
          <div style={{
            background: T.ink, borderRadius: 20, padding: 36,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <div style={{
                width: 7, height: 7, background: T.white,
                borderRadius: '50%', animation: 'pulse 2s infinite',
              }} />
              <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Live Übersicht — Demo-Portfolio</span>
            </div>

            <div style={{ fontSize: 22, fontWeight: 700, color: T.white, marginBottom: 6 }}>
              Alles im Griff.
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55, marginBottom: 22 }}>
              Zahlungen, Mieter und Mahnungen — zentral auf einem Dashboard.
            </p>

            {/* KPI row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {[
                { label: 'Einnahmen Apr.', value: '3.240 €' },
                { label: 'Mieteingänge', value: '8 / 8' },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px',
                }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: T.white, letterSpacing: '-1px' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Status list */}
            {[
              { label: 'Hauptstraße 12, Whg. 3', status: 'Vermietet' },
              { label: 'Mahnung – Müller, Apr.', status: 'Versendet' },
              { label: 'Nebenkosten 2025', status: 'In Arbeit' },
            ].map(({ label, status }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>{label}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: T.white,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 100, padding: '2px 10px',
                }}>
                  {status}
                </span>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 16 }}>
              {['Kein Vertrag', 'DSGVO-konform', 'Deutsche Server'].map(item => (
                <span key={item} style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ STAT STRIP ══════════ */}
      <div style={{ borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        }} className="lp-stat-grid">
          {[
            { num: '500+',    text: 'Vermieter verwalten bereits mit ImmoHQ ihr Portfolio.' },
            { num: '50.000+', text: 'Mieteinheiten werden monatlich zentral verwaltet.' },
            { num: '4.9 / 5', text: 'Kundenzufriedenheit — bewertet von echten Nutzern.' },
          ].map(({ num, text }, i) => (
            <div key={num} style={{
              padding: 'clamp(32px,4vw,48px) clamp(24px,4vw,48px)',
              borderRight: i < 2 ? `1px solid ${T.border}` : 'none',
            }}>
              <div style={{ fontSize: 'clamp(40px,4.5vw,56px)', fontWeight: 800, letterSpacing: '-3px', color: T.ink, lineHeight: 1, marginBottom: 10 }}>
                {num}
              </div>
              <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.6, maxWidth: 240 }}>
                {text}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ BENEFITS ══════════ */}
      <section style={{
        background: T.white,
        padding: 'clamp(56px,8vw,80px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <Pill>Warum ImmoHQ</Pill>
            <h2 style={{
              fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 800,
              letterSpacing: '-1.5px', color: T.ink, margin: 0, lineHeight: 1.15,
            }}>
              Weniger Aufwand. Mehr Überblick.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="lp-feat-grid">
            {[
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
                title: 'Sieh sofort, wer gezahlt hat und wer nicht',
                desc: 'Alle Mietzahlungen übersichtlich nach Status — offen, bezahlt, überfällig. Auf einen Blick, ohne Suchen.',
              },
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
                title: 'Alle Objekte und Mieter an einem Ort',
                desc: 'Kein Wechsel zwischen Ordnern, Tabellen oder Tools. Objekte, Einheiten und Mieter zentral verwalten.',
              },
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
                title: 'Keine Excel-Listen mehr',
                desc: 'Schluss mit manuellen Tabellen und doppelter Datenpflege. Alles läuft strukturiert in ImmoHQ.',
              },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{
                background: T.bg, border: `1.5px solid ${T.border}`,
                borderRadius: 16, padding: 'clamp(24px,3vw,32px)',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: T.white, border: `1px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  {icon}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 8, letterSpacing: '-0.3px' }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
                  {desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ PRODUKTVORSCHAU ══════════ */}
      <section style={{
        background: T.bg,
        padding: 'clamp(56px,8vw,80px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <Pill>Produktvorschau</Pill>
            <h2 style={{
              fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 800,
              letterSpacing: '-1.5px', color: T.ink, margin: '0 0 12px', lineHeight: 1.15,
            }}>
              So sieht ImmoHQ aus
            </h2>
            <p style={{ fontSize: 16, color: T.muted, margin: 0, lineHeight: 1.65 }}>
              Alle wichtigen Informationen auf einen Blick – einfach und übersichtlich.
            </p>
          </div>

          {/* Mock Dashboard — full layout with sidebar */}
          <div style={{
            borderRadius: isMobile ? 14 : 20, border: `1.5px solid ${T.border}`,
            boxShadow: '0 20px 64px rgba(0,0,0,0.13)', overflow: 'hidden',
            maxWidth: 1040, margin: '0 auto',
            display: 'flex', height: isMobile ? 420 : 560,
          }}>

            {/* ── Sidebar (desktop only) ── */}
            {!isMobile && <div style={{
              width: 200, background: '#111', flexShrink: 0,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }} className="lp-mock-sidebar">
              {/* Logo */}
              <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '1.5px', color: 'white' }}>IMMO<span style={{ color: 'white' }}>HQ</span></span>
              </div>
              {/* Nav items */}
              <div style={{ flex: 1, padding: '6px 10px', overflowY: 'auto' }}>
                {[
                  { label: 'Dashboard', active: false },
                  { label: 'Objekte', active: false },
                  { label: 'Mieter', active: false },
                  { label: 'Zahlungen', active: true },
                  null,
                  { label: 'Vorgänge', active: false },
                  { label: 'Zähler', active: false },
                  { label: 'Nebenkosten', active: false },
                  { label: 'Kontakte', active: false },
                  { label: 'Mietübersicht', active: false },
                  null,
                  { label: 'Finanzen', active: false, group: 'FINANZEN' },
                  null,
                  { label: 'Nachrichten', active: false, group: 'KOMMUNIKATION' },
                  { label: 'Dokumente', active: false },
                ].map((item, i) => {
                  if (item === null) return (
                    <div key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '6px 2px' }} />
                  );
                  return (
                    <div key={item.label}>
                      {item.group && (
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', padding: '6px 8px 3px' }}>
                          {item.group}
                        </div>
                      )}
                      <div style={{
                        padding: '7px 10px', borderRadius: 7, marginBottom: 1,
                        background: item.active ? 'rgba(255,255,255,0.12)' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <span style={{ fontSize: 12, fontWeight: item.active ? 700 : 500, color: item.active ? 'white' : 'rgba(255,255,255,0.5)' }}>
                          {item.label}
                        </span>
                        {item.active && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'white', flexShrink: 0 }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Footer */}
              <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'white' }}>MK</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Max Koch</div>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 100 }}>Starter</span>
                  </div>
                </div>
              </div>
            </div>}

            {/* ── Main content ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f7f7f7', overflow: 'hidden' }}>
              {/* Topbar */}
              <div style={{
                background: 'white', borderBottom: '1px solid #e5e5e5',
                padding: '0 20px', height: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>Zahlungen</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>Mi., 15. Apr.</span>
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
                {/* KPI row */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 8 : 10, marginBottom: 18 }}>
                  {[
                    { label: 'Einnahmen April', value: '4.620 €' },
                    { label: 'Mieteingänge', value: '6 / 8' },
                    { label: 'Offene Posten', value: '2' },
                    { label: 'Aktive Mieter', value: '8' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: 'white', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5e5e5' }}>
                      <div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 5 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111', letterSpacing: '-0.8px' }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Table */}
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
                  {!isMobile && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.6fr 1fr 1fr 1.5fr', padding: '9px 14px', background: '#f7f7f7', borderBottom: '1px solid #e5e5e5' }}>
                    {['Mieter', 'Objekt / Einheit', 'Monat', 'Betrag', 'Status'].map(h => (
                      <span key={h} style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
                    ))}
                  </div>
                  )}
                  {[
                    { name: 'Anna Becker', obj: 'Hauptstr. 12 · Whg. 2', amount: '790 €', status: 'Bezahlt', ok: true },
                    { name: 'Max Müller', obj: 'Hauptstr. 12 · Whg. 3', amount: '650 €', status: 'Offen', ok: false },
                    { name: 'Lisa Wagner', obj: 'Bergweg 5 · EG', amount: '920 €', status: 'Bezahlt', ok: true },
                    { name: 'Jonas Krause', obj: 'Bergweg 5 · OG', amount: '840 €', status: 'Überfällig', ok: false },
                    { name: 'Maria Hoffmann', obj: 'Seestr. 8 · Whg. 1', amount: '710 €', status: 'Bezahlt', ok: true },
                  ].map(({ name, obj, amount, status, ok }, i, arr) => (
                    isMobile ? (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{name}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>{obj}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{amount}</div>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 20, background: ok ? '#dcfce7' : status === 'Überfällig' ? '#fee2e2' : '#fef9c3', color: ok ? '#15803d' : status === 'Überfällig' ? '#b91c1c' : '#854d0e' }}>{status}</span>
                      </div>
                    </div>
                    ) : (
                    <div key={name} style={{
                      display: 'grid', gridTemplateColumns: '2fr 1.6fr 1fr 1fr 1.5fr',
                      padding: '11px 14px', alignItems: 'center',
                      borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{name}</span>
                      <span style={{ fontSize: 11, color: '#888' }}>{obj}</span>
                      <span style={{ fontSize: 11, color: '#888' }}>Apr. 2026</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{amount}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: ok ? '#dcfce7' : status === 'Überfällig' ? '#fee2e2' : '#fef9c3',
                          color: ok ? '#15803d' : status === 'Überfällig' ? '#b91c1c' : '#854d0e',
                        }}>{status}</span>
                        {!ok && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                            border: '1px solid #e5e5e5', background: 'white', color: '#111',
                          }}>
                            {status === 'Überfällig' ? 'Mahnung' : 'Bezahlt ✓'}
                          </span>
                        )}
                      </div>
                    </div>
                    )
                  ))}
                </div>
                <p style={{ fontSize: 10, color: '#d1d5db', textAlign: 'center', margin: '14px 0 0' }}>
                  Fiktive Testdaten zur Illustration
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ IMMOBILIEN-TYPEN SLIDESHOW ══════════ */}
      <section style={{ background: T.white, padding: 'clamp(56px,8vw,80px) 0', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px,5vw,40px)', marginBottom: 36 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <Pill>Für jede Immobilie</Pill>
              <h2 style={{ fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 800, letterSpacing: '-1.5px', color: T.ink, margin: 0, lineHeight: 1.15 }}>
                Egal ob Altbau oder Neubau —<br />ImmoHQ passt sich an.
              </h2>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[-1, 1].map(dir => (
                <button key={dir} onClick={() => slideScroll(dir)} style={{
                  width: 44, height: 44, borderRadius: '50%',
                  border: `1.5px solid ${T.border}`,
                  background: T.white, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.ink; e.currentTarget.querySelector('svg').style.stroke = T.white; }}
                  onMouseLeave={e => { e.currentTarget.style.background = T.white; e.currentTarget.querySelector('svg').style.stroke = T.ink; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke 0.15s' }}>
                    {dir === -1
                      ? <polyline points="15 18 9 12 15 6"/>
                      : <polyline points="9 18 15 12 9 6"/>
                    }
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable track */}
        <div
          ref={slideRef}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          style={{
            display: 'flex', gap: 16,
            overflowX: 'auto', cursor: 'grab',
            paddingLeft: 'clamp(16px,5vw,40px)',
            paddingRight: 'clamp(16px,5vw,40px)',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            userSelect: 'none',
          }}
        >
          <style>{`.lp-slide::-webkit-scrollbar{display:none}`}</style>
          {IMMOBILIEN_TYPES.map(({ slug, label, sub, img }) => (
            <Link key={label} to={`/immobilien/${slug}`} style={{
              flexShrink: 0,
              width: 320, height: 420,
              borderRadius: 18, overflow: 'hidden',
              position: 'relative',
              textDecoration: 'none', display: 'block',
            }}>
              <img
                src={img}
                alt={label}
                draggable={false}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover', display: 'block',
                  pointerEvents: 'none',
                }}
              />
              {/* Gradient overlay */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)',
              }} />
              {/* Label */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 24px 22px' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                  {sub}
                </div>
              </div>
            </Link>
          ))}
          {/* trailing spacer */}
          <div style={{ flexShrink: 0, width: 'clamp(0px,2vw,24px)' }} />
        </div>
      </section>

      {/* ══════════ WARUM — DARK SECTION ══════════ */}
      <section style={{ background: T.ink, padding: 'clamp(72px,10vw,96px) clamp(16px,5vw,40px)' }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 80, alignItems: 'start',
        }} className="lp-2col-grid">

          {/* Left quote */}
          <div style={{
            fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 700, color: T.white,
            lineHeight: 1.25, letterSpacing: '-0.5px',
          }}>
            Vermietung ist zu wichtig
            für Excel-Tabellen und manuelle Mahnungen.
          </div>

          {/* Right numbered list */}
          <div>
            {[
              { title: 'Zahlungen einfach erfassen und verfolgen', desc: 'Offene Posten auf einen Blick sehen — für jede Einheit, jeden Monat.' },
              { title: 'Mahnungen schnell erstellen und versenden', desc: 'Mahnung in wenigen Klicks erstellen und direkt aus dem System versenden.' },
              { title: 'Betriebskosten strukturiert erfassen und vorbereiten', desc: 'Kosten erfassen, Schlüssel anwenden, PDF erstellen — ohne Steuerberater-Wissen.' },
              { title: 'Voller Überblick jederzeit — alle Daten zentral an einem Ort', desc: 'Einnahmen, Einheiten, Mieter — alles auf einem Dashboard. Mobil und am Rechner.' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: 20, alignItems: 'flex-start',
                padding: '22px 0', borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.2)',
                  letterSpacing: '1px', width: 24, flexShrink: 0, paddingTop: 3,
                }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: T.white, marginBottom: 5 }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65 }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ ANWENDUNGSFÄLLE — DARK ══════════ */}
      <section style={{ background: T.ink, padding: '0 clamp(16px,5vw,40px) clamp(72px,10vw,96px)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            paddingBottom: 48, borderBottom: '1px solid rgba(255,255,255,0.08)',
            flexWrap: 'wrap', gap: 20,
          }}>
            <div>
              <h2 style={{
                fontSize: 'clamp(36px,4.5vw,56px)', fontWeight: 800,
                letterSpacing: '-2px', color: T.white, lineHeight: 1.05, margin: 0,
              }}>
                Für jeden Vermieter.
              </h2>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65, maxWidth: 440, margin: '12px 0 0' }}>
                Ob 2 oder 200 Einheiten — ImmoHQ passt sich deinem Portfolio an.
              </p>
            </div>
            <Link to="/register" style={{
              padding: '12px 24px', background: T.white, color: T.ink,
              borderRadius: 8, fontSize: 14, fontWeight: 700,
              textDecoration: 'none', transition: 'background 0.15s',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8e8e8'}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              Jetzt starten →
            </Link>
          </div>

          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }} className="lp-use-grid">
            {USE_CASES.map((uc, i) => (
              <div key={uc.title} style={{
                padding: 'clamp(28px,3vw,36px) clamp(24px,2.5vw,32px)',
                borderRight: (i + 1) % 3 !== 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>{uc.icon}</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: T.white }}>
                    {uc.title}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                  {uc.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FEATURES ══════════ */}
      <section id="features" style={{
        background: T.white,
        padding: 'clamp(64px,10vw,104px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,6vw,64px)' }}>
            <Pill>Features</Pill>
            <h2 style={{
              fontSize: 'clamp(32px,4.5vw,52px)', fontWeight: 800,
              letterSpacing: '-2px', lineHeight: 1.08, margin: '0 auto 16px', maxWidth: 600,
            }}>
              Alles was du brauchst.
            </h2>
            <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.65, margin: '0 auto', maxWidth: 480 }}>
              Kein Feature-Bloat. Nur die Werkzeuge, die Vermieter und Hausverwalter wirklich brauchen.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="lp-feat-grid">
            {FEATURES.map(f => (
              <div key={f.title} style={{
                background: T.white, border: `1.5px solid ${T.border}`,
                borderRadius: 16, padding: 'clamp(24px,3vw,32px)',
                transition: 'box-shadow 0.15s, transform 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: T.bg, border: `1px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 18, color: T.ink,
                }}>
                  {f.icon}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 8, letterSpacing: '-0.3px' }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section id="how-it-works" style={{
        background: T.bg,
        padding: 'clamp(64px,10vw,104px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,6vw,64px)' }}>
            <Pill>So einfach</Pill>
            <h2 style={{
              fontSize: 'clamp(32px,4.5vw,52px)', fontWeight: 800,
              letterSpacing: '-2px', lineHeight: 1.08, margin: 0,
            }}>
              In drei Schritten startklar.
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="lp-steps-grid">
            {[
              {
                num: '01',
                title: 'Konto erstellen',
                desc: 'Kostenlos registrieren. Kein Vertrag, keine Kreditkarte. In 60 Sekunden fertig.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
              },
              {
                num: '02',
                title: 'Objekte anlegen',
                desc: 'Immobilien, Einheiten und Mieter erfassen. Der Assistent führt dich Schritt für Schritt.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
              },
              {
                num: '03',
                title: 'Einfach verwalten',
                desc: 'Zahlungen eintragen, Mahnungen erstellen und alles im Blick behalten — an einem Ort.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
              },
            ].map(({ num, title, desc, icon }) => (
              <div key={num} style={{
                background: T.white, border: `1.5px solid ${T.border}`,
                borderRadius: 16, padding: 'clamp(28px,4vw,40px)',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: -10, right: 14,
                  fontSize: 80, fontWeight: 800, letterSpacing: '-5px',
                  color: T.border, lineHeight: 1, userSelect: 'none',
                }}>
                  {num}
                </div>
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: T.white, border: `1.5px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20, position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  {icon}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 10, letterSpacing: '-0.3px' }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
                  {desc}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/register" style={{
              padding: '13px 28px', background: T.ink, color: T.white,
              borderRadius: 8, fontSize: 14, fontWeight: 700,
              textDecoration: 'none', display: 'inline-block', transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#333'}
              onMouseLeave={e => e.currentTarget.style.background = T.ink}
            >
              Jetzt in 2 Minuten starten →
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      <section id="pricing" style={{
        background: T.white,
        padding: 'clamp(64px,10vw,104px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,6vw,64px)' }}>
            <Pill>Preise</Pill>
            <h2 style={{
              fontSize: 'clamp(32px,4.5vw,52px)', fontWeight: 800,
              letterSpacing: '-2px', lineHeight: 1.08, margin: '0 0 16px',
            }}>
              Einfache, faire Preise.
            </h2>
            <p style={{ fontSize: 16, color: T.muted, margin: 0 }}>
              Keine versteckten Kosten. Monatlich kündbar. Kein Risiko.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, alignItems: 'start' }} className="lp-pricing-grid">
            {PLANS.map(plan => (
              <div key={plan.key} style={{
                background: plan.highlight ? T.ink : T.white,
                border: plan.highlight ? `1.5px solid ${T.ink}` : `1.5px solid ${T.border}`,
                borderRadius: 20,
                padding: 'clamp(28px,4vw,40px)',
                position: 'relative',
                boxShadow: plan.highlight ? '0 16px 48px rgba(17,24,39,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                transform: plan.highlight ? 'scale(1.02)' : 'none',
              }}>
                {plan.highlight && (
                  <div style={{
                    position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                    background: T.white, color: T.ink,
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase',
                    padding: '4px 14px', borderRadius: 100,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    whiteSpace: 'nowrap',
                  }}>
                    Beliebt
                  </div>
                )}

                <div style={{ marginBottom: 28 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
                    color: plan.highlight ? 'rgba(255,255,255,0.4)' : T.muted, marginBottom: 12,
                  }}>
                    {plan.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10 }}>
                    <span style={{
                      fontSize: 'clamp(36px,4vw,52px)', fontWeight: 800, letterSpacing: '-3px', lineHeight: 1,
                      color: plan.highlight ? T.white : T.ink,
                    }}>
                      {plan.price}
                    </span>
                    <span style={{ fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.35)' : T.muted }}>
                      {plan.period}
                    </span>
                  </div>
                  <p style={{ fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.45)' : T.muted, lineHeight: 1.6, margin: 0 }}>
                    {plan.desc}
                  </p>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {plan.features.map(({ label, ok }) => (
                    <li key={label} style={{
                      display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
                      color: plan.highlight
                        ? (ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)')
                        : (ok ? T.ink : '#bbb'),
                    }}>
                      <span style={{ flexShrink: 0 }}>
                        {ok
                          ? Ico.check({ color: plan.highlight ? 'rgba(255,255,255,0.6)' : T.ink })
                          : Ico.x
                        }
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>

                <Link to={plan.ctaLink} style={{
                  display: 'block', textAlign: 'center',
                  padding: '13px 0', borderRadius: 8,
                  background: plan.highlight ? T.white : T.ink,
                  color: plan.highlight ? T.ink : T.white,
                  textDecoration: 'none', fontSize: 14, fontWeight: 700,
                  transition: 'opacity 0.15s',
                  border: plan.highlight ? 'none' : 'none',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: T.muted, marginTop: 28 }}>
            Alle Pläne inkl. 14 Tage kostenloser Testphase · Keine Kreditkarte nötig
          </p>
        </div>
      </section>

      {/* ══════════ TESTIMONIALS ══════════ */}
      <section style={{
        background: T.bg,
        padding: 'clamp(64px,10vw,104px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,6vw,56px)' }}>
            <Pill>Referenzen</Pill>
            <h2 style={{
              fontSize: 'clamp(32px,4.5vw,52px)', fontWeight: 800,
              letterSpacing: '-2px', lineHeight: 1.08, margin: 0,
            }}>
              Was Vermieter sagen.
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="lp-feat-grid">
            {[
              { text: 'ImmoHQ hat mir unzählige Stunden Arbeit erspart. Mahnungen erstelle ich jetzt in Sekunden statt in Stunden.', name: 'Thomas K.', role: 'Vermieter · 12 Einheiten, München' },
              { text: 'Endlich eine Software, die sich nicht wie ein Bankprogramm aus den 90ern anfühlt. Klar, schnell, professionell.', name: 'Sarah M.', role: 'Hausverwalterin · 40+ Einheiten, Hamburg' },
              { text: 'Die Betriebskostenabrechnung hat mir früher Wochen gekostet. Mit ImmoHQ ist es eine Sache von Stunden.', name: 'Klaus R.', role: 'Privatvermieter · 6 Einheiten, Berlin' },
            ].map(({ text, name, role }) => (
              <div key={name} style={{
                background: T.white, border: `1.5px solid ${T.border}`,
                borderRadius: 16, padding: 'clamp(24px,3vw,32px)',
              }}>
                <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
                  {[0,1,2,3,4].map(i => (
                    <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  ))}
                </div>
                <p style={{ fontSize: 14, color: T.ink, lineHeight: 1.7, margin: '0 0 24px', fontStyle: 'italic' }}>
                  „{text}"
                </p>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{name}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section style={{
        background: T.bg,
        padding: 'clamp(64px,10vw,96px) clamp(16px,5vw,40px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px,5vw,48px)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: T.ink, marginBottom: 12 }}>FAQ</div>
            <h2 style={{
              fontSize: 'clamp(30px,3.5vw,44px)', fontWeight: 800,
              letterSpacing: '-1.5px', color: T.ink, margin: 0,
            }}>
              Deine Fragen.<br />Ehrliche Antworten.
            </h2>
          </div>

          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '20px 0',
                    fontSize: 15, fontWeight: 600, color: T.ink,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
                    background: 'none', border: 'none', cursor: 'pointer',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#444'}
                  onMouseLeave={e => e.currentTarget.style.color = T.ink}
                >
                  {item.q}
                  <div style={{
                    width: 26, height: 26, background: openFaq === i ? T.ink : '#e8e8e8',
                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'all 0.2s',
                    color: openFaq === i ? T.white : '#888',
                  }}>
                    {openFaq === i ? Ico.minus : Ico.plus}
                  </div>
                </button>
                {openFaq === i && (
                  <div style={{
                    fontSize: 15, color: '#666', lineHeight: 1.75, paddingBottom: 20,
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CTA ══════════ */}
      <section style={{
        background: T.ink,
        padding: 'clamp(64px,10vw,112px) clamp(16px,5vw,40px)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(36px,5vw,60px)', fontWeight: 800,
            letterSpacing: '-2.5px', lineHeight: 1.06, color: T.white, margin: '0 0 16px',
          }}>
            Starte heute.<br />Kostenlos.
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.4)', margin: '0 0 36px', lineHeight: 1.65 }}>
            500+ Vermieter verwalten bereits mit ImmoHQ. Kein Vertrag, kein Risiko.
          </p>
          <Link to="/register" style={{
            display: 'inline-block',
            padding: '16px 40px', background: T.white, color: T.ink,
            borderRadius: 8, fontSize: 17, fontWeight: 800,
            textDecoration: 'none', transition: 'background 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = '#e8e8e8'}
            onMouseLeave={e => e.currentTarget.style.background = T.white}
          >
            Kostenlos starten →
          </Link>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.25)', margin: '20px 0 0', letterSpacing: '0.2px' }}>
            Keine Kreditkarte · Keine Mindestlaufzeit · Jederzeit kündbar
          </p>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer style={{
        background: T.ink,
        borderTop: '1px solid rgba(255,255,255,0.07)',
        padding: 'clamp(48px,7vw,72px) clamp(16px,5vw,40px) clamp(28px,4vw,40px)',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Top */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: 40, marginBottom: 48,
          }} className="lp-footer-grid">

            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 32, height: 32, background: 'rgba(255,255,255,0.1)',
                  borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span style={{ fontSize: 17, fontWeight: 800, color: T.white }}>ImmoHQ</span>
              </div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, margin: 0, maxWidth: 280 }}>
                Professionelle Immobilienverwaltung für private Vermieter und Hausverwaltungen.
              </p>
            </div>

            {/* Produkt */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 16 }}>
                Produkt
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {[['#features', 'Features'], ['#pricing', 'Preise'], ['#how-it-works', 'So funktioniert\'s']].map(([href, label]) => (
                  <li key={href}>
                    <a href={href} style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.target.style.color = T.white}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Konto */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 16 }}>
                Konto
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {[['/register', 'Registrieren'], ['/login', 'Anmelden']].map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.target.style.color = T.white}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Rechtliches */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 16 }}>
                Rechtliches
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {[['/impressum', 'Impressum'], ['/datenschutz', 'Datenschutz']].map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.target.style.color = T.white}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a href="mailto:info@immohq.eu" style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)', textDecoration: 'none', transition: 'color 0.15s' }}
                    onMouseEnter={e => e.target.style.color = T.white}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                  >
                    Kontakt
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 20,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12,
          }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.25)' }}>
              © {new Date().getFullYear()} ImmoHQ — Professionelle Immobilienverwaltung
            </span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.18)' }}>
              Server in Deutschland · DSGVO-konform
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
}
